<template>
    <FormKit
        :type="type"
        :label="label"
        :validation="validation"
        :value="value"
        :name="name"
        :auto-height="autoHeight"
        :min-auto-height="minAutoHeight"
        :autocomplete="autocomplete"
        :help="help"
        :rows="rows"
        :classes="{
            message: 'text-bbk-claret text-2xs text-right lowercase whitespace-pre-line',
            outer: 'w-full mb-4 text-black formkit-data-invalid:text-bbk-claret',
            label: getLabelCssClass,
            inner: 'relative flex items-center border-b-[1px] border-current autofill:bg-white bg-pink',
            input: 'pl-0 w-full !bg-transparent autofill:bg-white -webkit-autofill:bg-white rounded-none border-0 appearance-none focus:!ring-0 focus:border-black peer',
            help: 'text-2xs text-end',
        }"
        :outer-class="outerClass"
        :input-class="inputClass"
        :label-class="labelClass"
        :focus="focus"
        :validation-messages="validationMessages"
    >
        <template #suffix="{ state }">
            <ShcUiIcon v-if="!state.valid && state.blurred" class="text-bbk-claret" name="exclamation" />
            <ShcUiIcon v-if="state.valid && state.blurred" class="text-bbk-dark-green" name="check" />
            <ShcUiIcon v-if="suffixIcon" class="cursor-pointer" :name="suffixIcon" @click="handleSuffixIconClick" />
            <ShcUiIcon
                v-if="hasInfoIcon && !state.blurred"
                class="cursor-pointer ml-2"
                name="info"
                :filled="isInfoIconVisible"
                @click="handleInfoIconClick"
            />
        </template>
        <template #help="{ state: { blurred, valid, validationVisible } }">
            <div v-show="!validationVisible" class="text-right text-2xs">
                {{ isInfoIconVisible ? infoText : !valid && blurred ? '' : helpText }}
            </div>
        </template>
    </FormKit>
</template>

<script lang="ts" setup>
    import type { Props } from './props'

    defineOptions({ inheritAttrs: false })

    const props = withDefaults(defineProps<Props>(), {
        type: 'text',
    })

    const emit = defineEmits<{
        suffixIconClick: [payload: Pick<Props, 'suffixIcon' | 'type'>]
    }>()

    const { $i18n } = useNuxtApp()

    const isInfoIconVisible = ref(false)
    const isValidationRequired =
        (typeof props.validation === 'string' &&
            props.validation.includes('required') &&
            !props.validation.includes('required_once')) ||
        (Array.isArray(props.validation) &&
            props.validation.flat().includes('required') &&
            !props.validation.flat().includes('required_once'))

    const getLabelCssClass = computed(() => {
        const cssClass =
            'pl-0 left-0 absolute text-current lowercase font-normal text-base bg-pink transition-all duration-[0.2s] ease-[ease-out] delay-[0s] delay-[0.1s] bottom-[calc(100%_-_0.5em)] translate-y-full peer-focus:text-2xs peer-focus:translate-y-2/4 peer-focus:z-[2] peer-focus:delay-[0s] peer-focus:duration-[0.25s] peer-focus:bottom-full formkit-has-value:text-2xs formkit-has-value:translate-y-2/4 formkit-has-value:z-[2] formkit-has-value:delay-[0s] formkit-has-value:duration-[0.25s] formkit-has-value:bottom-full'

        if (isValidationRequired) {
            return `${cssClass} after:content-['*'] peer-focus:after:content-['']`
        }

        return cssClass
    })

    const validationMessages = computed(() => {
        const baseMessages = {
            required: $i18n.t('Bbk.form.validator.required'),
            matches: $i18n.t('Bbk.form.validator.matches'),
            confirm: $i18n.t('Bbk.form.validator.confirm'),
            email: $i18n.t('Bbk.form.validator.email'),
            contains_uppercase: $i18n.t('Bbk.form.validator.containsUppercase'),
            contains_lowercase: $i18n.t('Bbk.form.validator.containsLowercase'),
            contains_numeric: $i18n.t('Bbk.form.validator.containsNumeric'),
            nip: $i18n.t('Bbk.form.validator.nip'),
        }

        if (props.validationMessages) {
            const { required, matches, confirm, email, contains_uppercase, contains_lowercase, contains_numeric } =
                props.validationMessages

            return {
                required: required ?? baseMessages.required,
                matches: matches ?? baseMessages.matches,
                confirm: confirm ?? baseMessages.confirm,
                email: email ?? baseMessages.email,
                contains_uppercase: contains_uppercase ?? baseMessages.contains_uppercase,
                contains_lowercase: contains_lowercase ?? baseMessages.contains_lowercase,
                contains_numeric: contains_numeric ?? baseMessages.contains_numeric,
            }
        }

        return baseMessages
    })

    const helpText = computed(() => {
        return props.help ? props.help : isValidationRequired ? $i18n.t('Bbk.form.validator.required') : ''
    })

    function handleInfoIconClick() {
        isInfoIconVisible.value = !isInfoIconVisible.value
    }

    function handleSuffixIconClick() {
        emit('suffixIconClick', {
            suffixIcon: props.suffixIcon,
            type: props.type,
        })
    }
</script>

<!-- chrome autofill blue color workaround -->
<style lang="scss">
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
    }
</style>
